import { LottieComponentProps } from 'lottie-react';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import StartupAnimation from 'src/common/components/startup-animation/startup-animation';
import { delay } from 'src/common/helpers/delay-helper';
import {
  getLoadingAnimation,
  getTrainingModeAnimation,
} from 'src/common/helpers/startup-animation-helper';
import { getMetaData } from 'src/xlr-configuration/pos-service-type-utils';
import { RedirectContext, XLRStartSequenceState } from 'src/xlr-configuration/xlr-startup';
import XLRLogger from '../../common/helpers/logger';
import { skipAnimations } from '../../common/helpers/short-circuit-helper';
import { ILoginUser } from '../../common/interfaces/dtos/ILoginUser';
import { AppState } from '../../common/redux/configure-store';
import { LoginActions } from '../../common/redux/reducers/login-reducer';
import { initializationPostUserLogin } from '../business-logic/initialization-post-user-login';

const Initialize: React.FC<Initialize> = (props): JSX.Element => {
  const [animationDuration] = useState(props.postLoginInitialized ? 1000 : 7000);
  const defaultOptions: LottieComponentProps = {
    loop: false,
    autoplay: true,
    animationData: getLoadingAnimation(getMetaData().serviceType),
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const trainingModeOptions: LottieComponentProps = {
    loop: false,
    autoplay: true,
    animationData: getTrainingModeAnimation(getMetaData().serviceType),
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  const initialzed = useRef<boolean>(false);

  const { setLogin } = props;

  const { setStartupState } = useContext(RedirectContext);

  useEffect((): void => {
    const initialize = async (): Promise<void> => {
      return initializationPostUserLogin()
        .then((): void => {
          initialzed.current = true;
          setLogin({ initialized: true });
          if (skipAnimations()) {
            setStartupState(XLRStartSequenceState.Runtime);
          }
        })
        .catch(err => {
          XLRLogger.error({
            message: 'Failed to perform initializationPostUserLogin',
            source: 'initialize.tsx',
            data: err,
          });
          setStartupState(XLRStartSequenceState.Login);
        });
    };
    Promise.all([initialize(), delay(animationDuration)]).then(() => {
      setStartupState(XLRStartSequenceState.Runtime);
    });
  }, [setLogin, setStartupState, animationDuration]);

  return (
    <StartupAnimation
      lottieProps={props.trainingMode ? trainingModeOptions : defaultOptions}
      hideAnimation={!props.trainingMode}
    />
  );
};

export interface Initialize {
  trainingMode: boolean;
  setLogin: (user: Partial<ILoginUser>) => void;
  postLoginInitialized: boolean;
}

const mapStateToProps = (state: AppState) => ({
  trainingMode: state.posMode.trainingMode,
  postLoginInitialized: state.posMode.postLoginInitialized,
});

const mapDispatchToProps = {
  setLogin: LoginActions.setLogin,
};

export default connect(mapStateToProps, mapDispatchToProps)(Initialize);
